import {
  ChangeDetectorRef,
  Component,
  Directive,
  inject,
  Inject,
  InjectionToken,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  NgModel,
} from '@angular/forms';
import { Logger, LoggerService } from '@shared/utils';
import { noop, Subject, takeUntil, tap } from 'rxjs';
import { CONTROL_VALUE_ACCESSOR_CONFIG, ControlValueAccessorConfig } from './control-value-accessor.connector';

@Directive() // Implementace základní třídy, proto directive
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class BaseControlValueAccessor<T> implements ControlValueAccessor, OnInit, OnDestroy {
  readonly formControlDirective = viewChild(FormControlDirective);

  #control: FormControl = new FormControl();
  protected value = signal<null | T>(null);
  protected disabled = signal<boolean | T>(false);
  protected readonly destroy$ = new Subject<void>();
  protected readonly loggerService = inject(LoggerService);
  protected readonly cdRef = inject(ChangeDetectorRef);
  protected logger: Logger = this.loggerService.createLogger('BaseControlValueAccessor');

  constructor(
    private injector: Injector,
    @Optional() @Inject(CONTROL_VALUE_ACCESSOR_CONFIG) protected config?: Partial<ControlValueAccessorConfig>
  ) {}

  public ngOnInit(): void {
    this.setComponentControl();
    this.logger.debug('ngOnInit>> formControlDirective:', this.formControlDirective());
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get controlContainer() {
    return this.injector.get(ControlContainer);
  }

  get control() {
    return this.#control;
  }

  set control(control: FormControl) {
    this.#control = control;
    this.logger.debug('set-control:', control);
  }

  // #region -------------------------------------------------------- ControlValueAccessor Implementation --------------------------------------------------------
  // Prázdné funkce, které se zaregistrují pro změny hodnoty a dotyk
  // ted se nepozivaji, je reseno pomoci formControlDirective
  onChange: (value: T | null) => void = noop;
  onTouched: () => void = noop;

  // Registrace callbacku pro dotyk komponenty
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Registrace callbacku pro změny hodnoty
  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  // Metoda pro zápis hodnoty do komponenty z vnějšku
  writeValue(value: T): void {
    this.value.set(value);
    this.cdRef.detectChanges();
  }

  // Nastavení writeValue jako abstraktní metody
  // abstract writeValue(value: T): void;

  // Metoda pro disabled stav
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
  // endregion ------------------------------------------------------ ControlValueAccessor Implementation --------------------------------------------------------

  // #region -------------------------------------------------------- Private Methods --------------------------------------------------------
  private setComponentControl(): void {
    try {
      // ⬇ It can cause a circular dependency if injected in the constructor
      const ngControl = this.injector.get(NgControl, null, { self: true, optional: true });

      switch (ngControl?.constructor) {
        case NgModel: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { control, update, ...ngModel } = ngControl as NgModel;
          this.control = control;

          // this.subscribeToControlValueChangesWithModel(update);
          break;
        }
        case FormControlName: {
          this.control = this.injector.get(FormGroupDirective).getControl(ngControl as FormControlName);
          break;
        }
        default: {
          this.control = (ngControl as FormControlDirective).form as FormControl;
          break;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      this.control = new FormControl();
    }

    // this.subscribeToControlValueChanges();
  }

  // Specifické sledování změn hodnoty pro NgModel
  private subscribeToControlValueChangesWithModel(update: { emit: (value: T) => void }): void {
    this.control.valueChanges
      .pipe(
        tap((value: T) => update.emit(value)),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => this.logger.debug('Model value changed:', value));
  }

  // Sledování změn hodnoty kontrolu
  private subscribeToControlValueChanges(): void {
    this.control.valueChanges
      .pipe(
        tap((value) => this.logger.debug('Control value changed:', value)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  withFormControlDirective(callback: (valueAccessor: ControlValueAccessor) => void): void {
    const formControlDirective = this.formControlDirective();
    if (formControlDirective?.valueAccessor) {
      callback(formControlDirective.valueAccessor);
    } else {
      this.logger.warn('FormControlDirective not found', callback);
    }
  }
  // #endregion -------------------------------------------------------- Private Methods --------------------------------------------------------
}
